import { useEffect, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  getAdminTrackerInspect,
  type AdminTickerInspect,
} from "@/lib/api";

/** TrackerInspector answers "what is the tracker polling right now,
 *  and for whom?" The union of every org's baseline, one row per
 *  ticker, with orgs that care and the latest Snapshot if one has
 *  landed. Useful for debugging the consolidated single-tracker
 *  flow when a tenant reports stale cells. */
export function TrackerInspector() {
  const [tickers, setTickers] = useState<AdminTickerInspect[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    void getAdminTrackerInspect()
      .then((r) => {
        setTickers(r.tickers ?? []);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tracker inspector</CardTitle>
        <p className="text-xs text-muted-foreground">
          Every ticker the process-wide poll loop fetches, with the
          orgs subscribed to it. Refreshes every 10s.
        </p>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-rose-500">{error}</p>}
        {loading && tickers.length === 0 && (
          <p className="text-xs text-muted-foreground">Loading…</p>
        )}
        {tickers.length === 0 && !loading && !error && (
          <p className="text-xs text-muted-foreground">
            No tickers under active tracking.
          </p>
        )}
        {tickers.length > 0 && (
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-xs font-mono">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-4">Ticker</th>
                  <th className="py-1 pr-4">Orgs</th>
                  <th className="py-1 pr-4">State</th>
                  <th className="py-1 pr-4 text-right">Live</th>
                  <th className="py-1 pr-4 text-right">Prior</th>
                </tr>
              </thead>
              <tbody>
                {tickers.map((r) => {
                  const state = (r.latest?.state as string | undefined) ?? "—";
                  const live =
                    typeof r.latest?.live_price === "number"
                      ? r.latest.live_price.toFixed(2)
                      : "—";
                  const prior =
                    typeof r.latest?.prior_close === "number"
                      ? r.latest.prior_close.toFixed(2)
                      : "—";
                  return (
                    <tr key={r.ticker} className="border-t">
                      <td className="py-1 pr-4">{r.ticker}</td>
                      <td className="py-1 pr-4 truncate" title={r.orgs.join(", ")}>
                        {r.orgs.length === 0 ? "—" : r.orgs.join(", ")}
                      </td>
                      <td className="py-1 pr-4">{state}</td>
                      <td className="py-1 pr-4 text-right">{live}</td>
                      <td className="py-1 pr-4 text-right">{prior}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
